import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SEO, seoConfig } from "@/hooks/useSEO";

// Validation constraints (same as backend)
const CONSTRAINTS = {
  name: { min: 2, max: 80 },
  email: { max: 120 },
  message: { min: 10, max: 2000 },
  maxUrls: 2,
};

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Count URLs in text
  const countUrls = (text: string): number => {
    const urlPattern = /https?:\/\//gi;
    return (text.match(urlPattern) || []).length;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    // Validation des champs obligatoires
    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      toast({
        title: "Champs manquants",
        description: "Veuillez remplir tous les champs avant d'envoyer le message.",
        variant: "destructive",
      });
      return;
    }

    // Validation des longueurs
    if (trimmedName.length < CONSTRAINTS.name.min || trimmedName.length > CONSTRAINTS.name.max) {
      toast({
        title: "Nom invalide",
        description: `Le nom doit contenir entre ${CONSTRAINTS.name.min} et ${CONSTRAINTS.name.max} caractères.`,
        variant: "destructive",
      });
      return;
    }

    if (trimmedEmail.length > CONSTRAINTS.email.max) {
      toast({
        title: "Email trop long",
        description: `L'email ne doit pas dépasser ${CONSTRAINTS.email.max} caractères.`,
        variant: "destructive",
      });
      return;
    }

    if (trimmedMessage.length < CONSTRAINTS.message.min || trimmedMessage.length > CONSTRAINTS.message.max) {
      toast({
        title: "Message invalide",
        description: `Le message doit contenir entre ${CONSTRAINTS.message.min} et ${CONSTRAINTS.message.max} caractères.`,
        variant: "destructive",
      });
      return;
    }

    // Validation du format email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast({
        title: "Email invalide",
        description: "Veuillez entrer une adresse e-mail valide.",
        variant: "destructive",
      });
      return;
    }

    // Validation anti-spam (URLs)
    if (countUrls(trimmedMessage) > CONSTRAINTS.maxUrls) {
      toast({
        title: "Message invalide",
        description: "Votre message semble invalide. Merci de vérifier le contenu.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("submit-contact", {
        body: {
          name: trimmedName,
          email: trimmedEmail,
          message: trimmedMessage,
          honeypot: honeypot,
        },
      });

      if (error) throw error;

      // Handle response codes
      if (data?.error === "rate_limited") {
        toast({
          title: "Limite atteinte",
          description: "Vous avez déjà envoyé un message récemment. Réessayez dans 24h.",
          variant: "destructive",
        });
        return;
      }

      if (data?.error === "validation_error") {
        toast({
          title: "Message invalide",
          description: "Votre message semble invalide. Merci de vérifier le contenu.",
          variant: "destructive",
        });
        return;
      }

      if (data?.error === "server_error") {
        throw new Error("Server error");
      }

      // Success (including silent honeypot success)
      toast({
        title: "Message envoyé",
        description: "Message envoyé avec succès. Nous vous répondrons rapidement.",
      });

      // Reset du formulaire
      setName("");
      setEmail("");
      setMessage("");
      setHoneypot("");
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.contact} />
      <Header />

      <main className="flex-1">
        {/* Section Introduction */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Badge */}
            <div className="flex justify-center mb-6">
              <span className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#074155] bg-white text-sm font-semibold text-[#074155]">
                Formulaire
              </span>
            </div>

            {/* Titre */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-4">
              Contactez-nous
            </h1>

            {/* Texte descriptif */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] max-w-2xl mx-auto">
              Vous avez des questions sur <span className="font-semibold text-[#0c97a9]">Unotif</span> ? N'hésitez pas à nous contacter. Envoyez-nous un message et nous vous répondrons dans les plus brefs délais.
            </p>
          </div>
        </section>

        {/* Section Formulaire */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Card statique - même style que FAQ.tsx */}
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.04)]">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot - invisible pour les humains */}
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                
                {/* Champ Nom */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#022226] font-semibold">
                    Nom
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Votre nom"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="border-gray-200 focus:border-[#0C94A6] focus:ring-[#0C94A6]"
                  />
                </div>

                {/* Champ E-mail */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#022226] font-semibold">
                    E-mail
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border-gray-200 focus:border-[#0C94A6] focus:ring-[#0C94A6]"
                  />
                </div>

                {/* Champ Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#022226] font-semibold">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Dites-nous comment nous pouvons vous aider..."
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="border-gray-200 focus:border-[#0C94A6] focus:ring-[#0C94A6] resize-none"
                  />
                </div>

                {/* Bouton - même style que Pricing.tsx ligne 322 */}
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg"
                >
                  {isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
